import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  CardBody,
  Col,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import FalconCardHeader from 'components/common/FalconCardHeader';
import useApi from 'hooks/useApi';
import {
  getExportAllData,
  getFarmTableData
} from 'api/endpoints/TerrestrialServices';
import { get, isEmpty } from 'lodash';
import * as XLSX from 'xlsx';
import DynamicReportTable from './DynamicReportTable';
import { useNavigate } from 'react-router-dom';
import { getTenantFromToken } from 'utils/routeUtils';
import { SERVICES } from 'api/common/constants';

function DynamicReport({
  apiPath,
  title,
  fileName,
  editPath,
  editKey,
  addPath,
  viewDetailsPath,
  buttons = [],
  forWeather = false,
  dropdownFilter = false,
  dropdownData = [],
  payloadmode = 'nested',
  dateFilter,
  dateRangePicker,
  globalSearch = true,
  district_ids = [],
  errorMsg,
  responsive = false,
  cardTittles = [],
  showHeader = true,
  showFooter = true,
  service = SERVICES.DIGITIZATION, // Default service
  limit
}) {
  const tenant = getTenantFromToken();
  const getExportAllApi = useApi(getExportAllData);
  const getFarmTableDataAPI = useApi(getFarmTableData);

  const [showFilter, setShowFilter] = useState(false);
  const [exportdata, setExportData] = useState(null);

  const [show, setShow] = useState(false);
  const [target, setTarget] = useState(null);
  const navigate = useNavigate();

  const handleFilterToggle = () => {
    setShowFilter(!showFilter);
  };

  // Update block list when blocks API data changes
  useEffect(() => {
    const data = get(getExportAllApi, 'data.results', []);
    if (!isEmpty(data)) {
      const s3Link = data; // Assuming the S3 link is in the first block
      downloadFile(s3Link);
    }
  }, [getExportAllApi.data]);

  // Function to download the file from S3 link
  const downloadFile = url => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', '');
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const handleDownload = () => {
    const excelData = convertToExcelData(exportdata, fileName);
    const filename = `${fileName}`;
    downloadExcel(excelData, filename);
  };

  const downloadExcel = (excelData, filename) => {
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    const totalColumns = excelData[2]?.length || 1;
    const lastCol = totalColumns + 2;
    // Merge the first row across all columns
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } }];
    const wb = XLSX.utils.book_new();
    const sheetName = filename.replace('.xlsx', '');
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, filename);
  };

  const convertToExcelData = (data, fileName) => {
    if (!data || data.length === 0) return [];
    const now = new Date();
    const timestamp = now
      .toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
      .replace(',', ' at');
    const readableName = fileName
      .replace('.xlsx', '')
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
    const reportTitle = `${readableName} - Generated on ${timestamp} from the FARMSIO Application`;

    const isSMSLogLike = data.some(
      row =>
        row.extra &&
        typeof row.extra === 'object' &&
        Array.isArray(row.extra.data)
    );

    let headers = [];
    let dataRows = [];

    if (isSMSLogLike) {
      const excludeKeys = ['id', 'action', 'modalButtons'];
      const extraColumns = new Set();

      data.forEach(row => {
        const extraData = row.extra?.data;
        if (Array.isArray(extraData)) {
          extraData.forEach(entry => {
            Object.keys(entry || {}).forEach(key => {
              const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1); // Capitalize the first letter
              extraColumns.add(capitalizedKey);
            });
          });
        }
      });

      const processedRows = [];

      data.forEach(row => {
        const baseRow = {};
        for (const key in row) {
          if (!excludeKeys.includes(key) && key !== 'extra') {
            baseRow[key] = row[key];
          }
        }

        const extraData = row.extra?.data;

        if (Array.isArray(extraData) && extraData.length > 0) {
          extraData.forEach(extraEntry => {
            const fullRow = { ...baseRow };
            [...extraColumns].forEach(col => {
              const originalKey = col.charAt(0).toLowerCase() + col.slice(1); // Convert back to match original keys
              fullRow[col] = extraEntry[originalKey] ?? '';
            });
            processedRows.push(fullRow);
          });
        } else {
          const fullRow = { ...baseRow };
          [...extraColumns].forEach(col => {
            fullRow[col] = '';
          });
          processedRows.push(fullRow);
        }
      });

      headers = Object.keys(processedRows[0] || {});
      dataRows = processedRows.map(row => headers.map(key => row[key] ?? ''));
    } else {
      headers = Object.keys(data[0] || {});
      dataRows = data.map(item => headers.map(header => item[header] ?? ''));
    }

    return [[reportTitle], [], headers, ...dataRows];
  };

  const handleOpenModal = async (mode, rowData) => {
    // Check if editKey is an array
    let params = '';
    if (Array.isArray(editKey)) {
      // Create dynamic params from the array
      params = editKey.map(key => rowData[key]).join('/');
    } else {
      // Use editKey as a string
      params = rowData[editKey];
    }
    navigate(`/${tenant}${editPath}${params}`);
  };

  const handleAddOpenModal = async (mode, rowData) => {
    let params = '';
    if (Array.isArray(editKey)) {
      // Create dynamic params from the array
      params = editKey.map(key => rowData[key]).join('/');
    } else {
      // Use editKey as a string
      params = rowData[editKey];
    }
    navigate(`/${tenant}${addPath}${params}`);
  };

  const handleViewDetails = async (mode, rowData) => {
    let params = '';
    if (Array.isArray(editKey)) {
      // Create dynamic params from the array
      params = editKey.map(key => rowData[key]).join('/');
    } else {
      // Use editKey as a string
      params = rowData[editKey];
    }
    navigate(`/${tenant}${viewDetailsPath}${params}`);
  };

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);

    // Clean up the event listener when the component is unmounted
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Conditional styles based on the window width
  const divStyle =
    windowWidth >= 768 ? { height: '85dvh', overflow: 'hidden' } : {};

  return (
    <div style={divStyle}>
      <DynamicReportTable
        // tableData={tableData.data}
        key={apiPath}
        hyperlink={false}
        showFilter={showFilter}
        responsive={responsive}
        cardTittles={cardTittles}
        setExportData={setExportData}
        onEdit={(_, rowData) => handleOpenModal('edit', rowData)}
        onAdd={(_, rowData) => handleAddOpenModal('add', rowData)}
        onViewDetails={(_, rowData) =>
          handleViewDetails('viewDetails', rowData)
        }
        apiPath={apiPath}
        forWeather={forWeather}
        dateFilter={dateFilter}
        dropdownFilter={dropdownFilter}
        dropdownData={dropdownData}
        payloadmode={payloadmode}
        district_ids={district_ids}
        errorMsg={errorMsg}
        globalSearch={globalSearch}
        dateRangePicker={dateRangePicker}
        showHeader={showHeader}
        showFooter={showFooter}
        limit={limit}
        buttons={buttons}
        title={title}
        handleDownload={handleDownload}
        service={service}
      />
    </div>
  );
}
DynamicReport.propTypes = {
  apiPath: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  fileName: PropTypes.string.isRequired,
  errorMsg: PropTypes.string,
  editPath: PropTypes.string,
  addPath: PropTypes.string,
  viewDetailsPath: PropTypes.string,
  editKey: PropTypes.string,
  forWeather: PropTypes.bool,
  service: PropTypes.string,
  dateFilter: PropTypes.bool,
  dropdownFilter: PropTypes.bool,
  payloadmode: PropTypes.string,
  dropdownData: PropTypes.arrayOf(PropTypes.object),
  dateRangePicker: PropTypes.bool,
  globalSearch: PropTypes.bool,
  showHeader: PropTypes.bool,
  showFooter: PropTypes.bool,
  limit: PropTypes.number,
  district_ids: PropTypes.arrayOf(PropTypes.number),
  responsive: PropTypes.bool,
  cardTittles: PropTypes.arrayOf(PropTypes.string),
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      enabled: PropTypes.bool,
      buttonName: PropTypes.string,
      redirectPath: PropTypes.string
    })
  )
};
export default DynamicReport;
